import { OnChangeParams, Select, SelectProps } from 'baseui/select';
import stylesColors from 'common/styles/styles.colors';
import * as React from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';

import CustomFormControl, {
  CustomFormControlProps,
} from '../custom-form-control';
import { BaseElementInputProps } from '../field-type';
import { FormContext } from '../form';
import { styles } from '../styles';

export interface SelectInputProps
  extends BaseElementInputProps,
    Omit<
      CustomFormControlProps,
      'name' | 'overrides' | 'positive' | 'error' | 'children'
    >,
    Omit<SelectProps, 'name'> {
  type: 'select';
  noErrorText?: boolean;
  noMargin?: boolean;
  labelRightEnhancer?: React.ReactNode;
  onAfterChange?: (value: OnChangeParams) => void;
  overrideOnChange?: (prevValue: any, value: any) => void;
  fieldType?: 'control' | 'uncontrol' | any;
}

const overridesStyle = ({ overrides, disabled }) => ({
  ...(disabled
    ? {
        Input: {
          component: () => null,
          style: styles.reset,
        },
        IconsContainer: {
          component: () => null,
          style: styles.reset,
        },
        ControlContainer: {
          // component: () => null,
          style: styles.reset,
        },
      }
    : {
        ControlContainer: {
          style: styles.active,
        },
      }),
  Placeholder: {
    style: {
      color: `${stylesColors.skyLight} !important`,
      '-webkit-text-fill-color': `${stylesColors.skyLight} !important`,
    },
  },
  Popover: {
    props: {
      overrides: {
        Body: {
          style: {
            zIndex: 1000,
          },
        },
      },
    },
  },
  Root: {
    style: {
      minWidth: '168px',
      ...(disabled ? styles.reset : styles.active),
    },
  },
  ValueContainer: {
    style: {
      paddingTop: '2px',
      paddingBottom: '2px',
      ...(disabled ? styles.reset : styles.active),
    },
  },
  Tag: {
    props: {
      closeable: !disabled,
      overrides: {
        Root: {
          style: ({ $theme }) => ({
            backgroundColor: $theme.colors.primaryDark,
            borderColor: $theme.colors.primaryDark,
            color: 'white',
            '-webkit-text-fill-color': `white !important`,
          }),
        },
        Text: {
          style: ({ $theme }) => ({
            maxWidth: 'unset',
          }),
        },
      },
    },
  },
  DropdownListItem: {
    style: ({ $theme }) => ({
      position: 'relative',
    }),
  },
  ...overrides,
});

export default function SelectInput(props: SelectInputProps) {
  const { control } = useFormContext<any>();
  const watchValue = useWatch({ control, name: props.name });
  const {
    field: { ref, ...field },
    fieldState,
  } = useController({ name: props.name });
  const {
    label,
    readOnly,
    noErrorText = false,
    noMargin = false,
    maxDropdownHeight = '200px',
    overrides,
    onChange,
    onAfterChange,
    labelRightEnhancer,
    disabled,
    overrideOnChange,
    fieldType = 'control',
    ...restProps
  } = props;
  const context = React.useContext(FormContext);

  const _selected = React.useMemo(() => {
    if (restProps.value) {
      return restProps.value;
    }
    const value = fieldType === 'control' ? field.value : watchValue;
    if (value || typeof value === 'boolean') {
      if (typeof value !== 'object') {
        const selected = (props?.options as any)?.find(
          (option: any) => option?.id === value,
        );
        return selected ? [selected] : undefined;
      } else {
        return value;
      }
    }

    return undefined;
  }, [field.value, fieldType, props?.options, restProps.value, watchValue]);

  if (readOnly) {
    const _selected = () => {
      if (restProps.value) {
        return restProps.value;
      }

      if (watchValue || typeof watchValue === 'boolean') {
        if (typeof watchValue !== 'object') {
          const selected = (props?.options as any)?.find(
            (option: any) => option?.id === watchValue,
          );
          return selected ? [selected] : undefined;
        } else {
          return watchValue;
        }
      }

      return undefined;
    };
    return (
      <CustomFormControl
        {...restProps}
        labelRightEnhancer={labelRightEnhancer}
        label={label}
        noMargin={noMargin}
      >
        <Select
          clearable={false}
          value={_selected() || '-'}
          overrides={overridesStyle({
            overrides,
            disabled: true,
          })}
        />
      </CustomFormControl>
    );
  }

  const shouldShowError = !!fieldState.error?.message;
  const errorText = noErrorText ? '' : fieldState.error?.message;

  const _onChange = (params) => {
    if (overrideOnChange) {
      overrideOnChange(field.value, params);
    } else if (onChange) {
      onChange(params);
    } else {
      if (props.creatable && props.multi) {
        field.onChange(params?.value);
      } else if (params.value.length && !(params?.value?.length > 1)) {
        field.onChange(params?.value[0]?.id);
      } else {
        if (!params.value.length) {
          field.onChange(undefined);
        } else {
          field.onChange(params?.value);
        }
      }
    }
    onAfterChange && onAfterChange(params);
    field.onBlur();
  };

  return (
    <CustomFormControl
      label={label}
      {...restProps}
      error={errorText?.toString()}
      noMargin={noMargin}
      labelRightEnhancer={labelRightEnhancer}
      disabled={!context.editable || disabled}
    >
      <Select
        inputRef={ref}
        {...field}
        {...restProps}
        maxDropdownHeight={maxDropdownHeight}
        value={_selected}
        onBlur={field.onBlur}
        closeOnSelect
        error={shouldShowError}
        onChange={_onChange}
        disabled={!context.editable || disabled}
        overrides={overridesStyle({
          overrides,
          disabled: !context.editable || disabled,
        })}
      />
    </CustomFormControl>
  );
}
